import { sentenceCase } from 'change-case';
import { createColumnHelper } from '@tanstack/react-table';
import { TableLink } from '@monorepo/base/src/components/table/table-link/table-link';
import { Tag } from '@monorepo/base/src/components/tag/tag';
import { IPerformanceItem, PerformanceModel } from '../../models/performance.model';
import { ITest, TestModel } from '../../models/test.model';
import routes from '../../routes/index.routes';
import { useTestActions } from '../actions/test.actions';
import { useWithPerformanceHeaders } from './with-performance.headers';
import { isSubRow } from '@monorepo/base/src/components/table/table';
import { IEntityHeaders, IMergedHeadersReturn } from './index.headers';
import { mapToTagStatus, Status, Statuses } from '../../enums/status';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../stores';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useEffect } from 'react';
import { QuickActionsMenu } from '@monorepo/base/src/components/table/quick-actions-menu/quick-actions-menu';
import { TableSelectionHeader, TableSelectionCell } from '@monorepo/base/src/components/table/table-selection-cell/table-selection-cell';
import { TableMergeColumn } from '@monorepo/base/src/components/table/table-merge-column/table-merge-column';
import { id } from '@monorepo/tools/src/lib/types/primitives';

const columnHelper = createColumnHelper<ITest & IPerformanceItem>();

const normalizeGroupName = (name?: string) => name?.trim().toUpperCase();

export const useTestHeaders = (props: IEntityHeaders): IMergedHeadersReturn<TestModel> => {
	const { summary } = props;
	const { EntityActions } = useTestActions();
	const mergedPerformanceHeaders = useWithPerformanceHeaders({ summaryItem: summary, withBidPredictionColumns: true }).columns;
	const { settingsStore, testStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const tableStore = settingsStore.pages.get(currentRouteWithoutSpecialChars)?.tableStore;
	const testList = testStore.getListStore().getList();

	const getTestId = (rowData: ITest & IPerformanceItem): id | undefined => {
		const performanceModel = new PerformanceModel(rowData);
		return performanceModel.testId || performanceModel.id;
	};
	const getTestGroupName = (rowData: ITest & IPerformanceItem): string | undefined => {
		const performanceModel = new PerformanceModel(rowData);
		return performanceModel.testGroupName;
	};
	const getTestPercentage = (rowData: ITest & IPerformanceItem): number => {
		const testId = getTestId(rowData);
		if (!testId) {
			return 0;
		}
		const test = testList.find(item => item.getId()?.toString() === testId.toString());
		return test?.getPercentage() || 0;
	};
	const getMatchedGroup = (rowData: ITest & IPerformanceItem, activeOnly = false) => {
		const groupName = normalizeGroupName(getTestGroupName(rowData));
		const testId = getTestId(rowData);
		if (!groupName || !testId) {
			return undefined;
		}

		const test = testList.find(item => item.getId()?.toString() === testId.toString());
		const groups = test?.getTestGroups() || [];
		const groupsByName = groups.filter(group => normalizeGroupName(group.getName()) === groupName);
		if (activeOnly) {
			return groupsByName.find(group => group.getStatus() === Statuses.Active);
		}
		return groupsByName.find(group => group.getStatus() === Statuses.Active) || groupsByName[0];
	};
	const getSubRowWeight = (rowData: ITest & IPerformanceItem): number | undefined => {
		return getMatchedGroup(rowData)?.getWeight();
	};
	const getSubRowStrategyId = (rowData: ITest & IPerformanceItem): number | undefined => {
		return getMatchedGroup(rowData)?.getStrategy()?.id;
	};
	const isControlGroup = (rowData: ITest & IPerformanceItem): boolean => {
		const groupName = normalizeGroupName(getTestGroupName(rowData));
		return groupName === 'A';
	};
	const getSubRowWeightPercentage = (rowData: ITest & IPerformanceItem): number | undefined => {
		const testId = getTestId(rowData);
		if (!testId) {
			return undefined;
		}
		const test = testList.find(item => item.getId()?.toString() === testId.toString());
		const groups = test?.getTestGroups() || [];
		const activeGroups = groups.filter(group => group.getStatus() === Statuses.Active);
		const groupName = normalizeGroupName(getTestGroupName(rowData));
		if (!groupName || activeGroups.length === 0) {
			return undefined;
		}

		const currentGroup = getMatchedGroup(rowData, true);
		if (!currentGroup) {
			return undefined;
		}

		const testPercentage = Math.max(0, Math.min(100, test?.getPercentage() || 0));
		const controlGroupPercentage = 100 - testPercentage;
		if (isControlGroup(rowData)) {
			return controlGroupPercentage;
		}

		const experimentGroups = activeGroups.filter(group => normalizeGroupName(group.getName()) !== 'A');
		const experimentTotalWeight = experimentGroups.reduce((acc, group) => acc + (group.getWeight() || 0), 0);
		if (experimentTotalWeight <= 0) {
			return undefined;
		}
		const currentWeight = currentGroup.getWeight() || 0;
		return (currentWeight / experimentTotalWeight) * testPercentage;
	};

	const checkIfGroupIsActive = (rowData: ITest & IPerformanceItem): boolean => {
		return Boolean(getMatchedGroup(rowData, true));
	};
	useEffect(() => {
		if (tableStore) {
			const defaultVisibility = {
				id: true,
				name: true,
				status: true,
				weightPercentage: true,
				strategyId: true,
				percentage: true,
				auctions: true,
				auctionsWon: true,
				wins: true,
				clicks: true,
				conversions: true,
				cost: true,
				conversionValue: true,
				winRate: true,
				ctr: true,
				lpCvr: true,
				roas: true,
				spm: true,
				gain: true,
				date: false,
				description: false,
				impressions: false,
				optCost: false,
				optGain: false,
				bidPredError: false,
				bidPredErrorWin: false,
				bidPredErrorLoss: false,
				actualWinRate: false,
			};
			tableStore.setDefaultColumnVisibility(defaultVisibility);

			const localStorageKey = `adx_table_column_visibility_${currentRouteWithoutSpecialChars}`;
			let localStorageVisibility: Record<string, boolean> = {};
			const rawLocalStorageVisibility = localStorage.getItem(localStorageKey);
			if (rawLocalStorageVisibility) {
				try {
					localStorageVisibility = JSON.parse(rawLocalStorageVisibility) as Record<string, boolean>;
				} catch {
					localStorageVisibility = {};
				}
			}

			const currentVisibility = tableStore.getColumnVisibility() || {};
			const mergedVisibility = { ...defaultVisibility, ...localStorageVisibility, ...currentVisibility };
			const hasMissingKeys = Object.keys(defaultVisibility).some(key => !(key in mergedVisibility));
			if (Object.keys(currentVisibility).length === 0 || hasMissingKeys) {
				tableStore.setColumnVisibility(mergedVisibility);
			}
		}
	}, [currentRouteWithoutSpecialChars, tableStore]);

	return {
		columns: [
			columnHelper.display({
				id: 'selection',
				enableHiding: false,
				header: headerProps => <TableSelectionHeader headerProps={headerProps} />,
				cell: cellProps => {
					const { row } = cellProps;
					const preference = new TestModel(row.original);

					if (isSubRow(row)) {
						return <span></span>;
					}
					if (!EntityActions) {
						return null;
					}
					return (
						<TableMergeColumn>
							<TableSelectionCell cellProps={cellProps} />
							<QuickActionsMenu>
								<EntityActions entity={preference} />
							</QuickActionsMenu>
						</TableMergeColumn>
					);
				},
			}),
			columnHelper.accessor('name', {
				header: 'Test Name',
				cell: info => {
					const { row } = info;
					const name = row.getValue('name') as string | JSX.Element;
					if (isSubRow(row)) {
						const performanceModel = new PerformanceModel(row.original);
						const groupTitle = `Group: ${performanceModel.testGroupName}`;
						if (typeof name === 'string') {
							return (
								<span>
									<div>{groupTitle}</div>
									<div style={{ paddingLeft: '10px' }}>{sentenceCase(name)}</div>
								</span>
							);
						} else {
							return (
								<span>
									<div>{groupTitle}</div>
									<div style={{ paddingLeft: '10px' }}>{name}</div>
								</span>
							);
						}
					}
					return <TableLink toEdit={routes.tests.edit(row.getValue('id'))}>{name}</TableLink>;
				},
			}),
			columnHelper.accessor('status', {
				header: 'Status',
				cell: info => {
					const { row } = info;
					if (isSubRow(row)) {
						return <span></span>;
					}
					const status = row.getValue('status') as string;
					return <Tag type={mapToTagStatus(status.toUpperCase() as Status)}>{row.getValue('status')}</Tag>;
				},
			}),
			columnHelper.accessor(row => (row as IPerformanceItem & { weightPercentage?: number }).weightPercentage, {
				id: 'weightPercentage',
				header: 'Weight Percentage',
				cell: info => {
					const { row } = info;
					if (!isSubRow(row)) {
						return <span></span>;
					}
					if (!checkIfGroupIsActive(row.original) && !isControlGroup(row.original)) {
						return <span></span>;
					}
					const weight = getSubRowWeight(row.original);
					if (!weight && !isControlGroup(row.original)) {
						return <span></span>;
					} else if (isControlGroup(row.original)) {
						const safePercentageValue = Math.max(0, Math.min(100, getTestPercentage(row.original)));
						return <span>{`${(100 - safePercentageValue).toFixed(1)}%`}</span>;
					}
					const weightPercentage = getSubRowWeightPercentage(row.original);
					const formattedPercentage = weightPercentage !== undefined ? `${weightPercentage.toFixed(1)}%` : '-';
					return <span>{weight ? `${formattedPercentage}` : formattedPercentage}</span>;
				},
			}),
			columnHelper.accessor('id', {
				header: 'Test ID',
			}),
			columnHelper.accessor('description', {
				header: 'Description',
			}),
			columnHelper.accessor(row => (row as IPerformanceItem & { strategyId?: number }).strategyId, {
				id: 'strategyId',
				header: 'Strategy ID',
				cell: info => {
					const { row } = info;
					if (!isSubRow(row)) {
						return <span></span>;
					}
					const strategyId = getSubRowStrategyId(row.original);
					return <span>{strategyId ?? '-'}</span>;
				},
			}),
			// columnHelper.accessor('percentage', {
			// 	header: 'Percentage',
			// 	cell: info => {
			// 		const { row } = info;
			// 		return <span>{row.getValue('percentage')}</span>;
			// 	},
			// }),
			...mergedPerformanceHeaders,
			// columnHelper.accessor('spm', {
			// 	header: 'SPM',
			// 	cell: info => {
			// 		const { row } = info;
			// 		return <span>{row.getValue('spm')}</span>;
			// 	},
			// }),
		],
	};
};
